"use client"

import * as React from "react"
import type { ReactNode } from "react"
import type { BranchFinanceBoardFigures, FinanceHomeFigures } from "@ibada/shared-types/overview"

import { HomeTile, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { BudgetLineType, BudgetScope } from "@/features/budgeting/model/budget-enums"
import { useBudgetPerformanceQuery } from "@/features/budgeting/services/budgets-query"
import type { FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import { BoardTileStates } from "@/features/finance/overview/board/board-tile-states"
import { usePeriodFallback } from "@/features/finance/overview/board/use-period-fallback"
import {
  BranchExpensesTile,
  BranchIncomeTile,
  BranchMarginTile,
  BranchTreasuryTile,
} from "@/features/finance/overview/branch-f1/components/branch-money-tiles"
import { BranchBudgetTile, BranchFundsTile, BranchPledgesTile } from "@/features/finance/overview/branch-f1/components/branch-plan-tiles"
import {
  BranchAttentionTile,
  BranchControlTile,
  BranchRecentTile,
  RECENT_OPERATION_ROWS,
} from "@/features/finance/overview/branch-f1/components/branch-status-tiles"
import { BranchReconciliationTile } from "@/features/finance/overview/branch-f1/components/branch-status-tiles"
import { useBoardWording } from "@/features/finance/overview/branch-f1/hooks/use-board-wording"
import { useBranchBoardQuery, useBranchTreasuryQuery } from "@/features/finance/overview/branch-f1/hooks/use-branch-board-queries"
import {
  budgetPacePercent,
  budgetRows,
  capitalize,
  isEmptyBreakdown,
  isZeroMoney,
  localDayBefore,
} from "@/features/finance/overview/branch-f1/lib/branch-f1-format"
import { ChurchTileCompactEmpty, ChurchTileWithheld } from "@/features/finance/overview/church-f3/components/church-tile-parts"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { FinanceWidgetId } from "@/features/finance/overview/lib/finance-widget-ids"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import { useRecentFinanceTransactionRows } from "@/features/finance/overview/widgets/recent-transactions-widget"
import { useFinanceInvestigationSignalsQuery } from "@/features/finance/services/finance-investigation-query"
import { useAccountingTrialBalanceQuery } from "@/features/finance/services/finance-query"
import { PledgeFulfillmentFilter, PledgeKind } from "@/features/pledges/model/pledge-types"
import { usePledgesQuery } from "@/features/pledges/services/pledges-query"
import { useLocale, useTranslations } from "@/i18n"

/**
 * The F1 tiles as dashboard widgets. Fixed heights make the engine's masonry rows line up like the design: the
 * treasury row is taller, the three rows under it share one height. A tile resized wider keeps its height.
 */
const TALL_ROW_TILES: ReadonlySet<FinanceWidgetId> = new Set([FinanceWidgetId.BranchTreasury, FinanceWidgetId.BranchAttention])

export const branchTileHeightClass = (id: FinanceWidgetId) => (TALL_ROW_TILES.has(id) ? "h-[21rem]" : "h-[18.5rem]")

type WidgetProps = { context?: FinanceSurfaceContext }

function TileStates(props: {
  id: FinanceWidgetId
  title: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  compact?: boolean
  children: () => ReactNode
}) {
  return <BoardTileStates {...props} testIdPrefix='branch' heightClass={branchTileHeightClass(props.id)} />
}

/** A tile whose figures the server withheld after the board was built (ADR-0024): say so, never show zeroes. */
function WithheldTile({ title }: { title: string }) {
  return (
    <HomeTile title={title}>
      <ChurchTileWithheld />
    </HomeTile>
  )
}

/** "Record an income" for a reader who may record in this branch; never while inspecting (read-only). */
function useRecordLink(): HomeTileLink | null {
  const t = useTranslations("finance.overview.church")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const href = scope.canEdit ? go.hrefFor(OverviewRoutes.createIncome) : null
  return href ? { label: t("compactEmpty.record"), href } : null
}

function useLink(adminPath: string, label: string): HomeTileLink | null {
  const go = useOverviewNav()
  const href = go.hrefFor(adminPath)
  return href ? { label, href } : null
}

/**
 * The board's own figures for the board's period, or for the previous comparable one when this tile has nothing in
 * the board's period yet. The fallback read is the same endpoint: same gate, same disclosure.
 */
function useBoardWithFallback(isEmpty: (figures: BranchFinanceBoardFigures) => boolean) {
  const tile = usePeriodFallback()
  const current = useBranchBoardQuery({ enabled: true })
  const figures = current.data?.figures ?? null
  const currentEmpty = figures !== null && isEmpty(figures)
  const previous = useBranchBoardQuery({ enabled: currentEmpty && tile.period !== null, period: tile.period ?? undefined })
  const previousFigures = previous.data?.figures ?? null
  const useFallback = currentEmpty && previousFigures !== null && !isEmpty(previousFigures)
  return {
    current,
    currency: current.data?.currency ?? "",
    withheld: current.data !== undefined && !current.data.disclosed,
    figures: useFallback ? previousFigures : figures,
    fallback: useFallback ? tile.fallback : null,
    shownPeriod: useFallback ? tile.period : null,
    bothEmpty: currentEmpty && !previous.isLoading && !useFallback,
    isLoading: current.isLoading || (currentEmpty && previous.isLoading),
  }
}

const periodIsEmpty = (figures: Pick<FinanceHomeFigures, "income" | "expenses" | "remittancesToParish">) =>
  isZeroMoney(figures.income) && isZeroMoney(figures.expenses) && isZeroMoney(figures.remittancesToParish)

export function BranchTreasuryWidget() {
  const t = useTranslations("finance.overview")
  const tile = usePeriodFallback()
  const current = useBranchTreasuryQuery({ enabled: true })
  const board = useBranchBoardQuery({ enabled: true })
  const figures = current.data?.figures ?? null
  const currentEmpty = figures !== null && periodIsEmpty(figures)
  const previous = useBranchTreasuryQuery({ enabled: currentEmpty && tile.period !== null, period: tile.period ?? undefined })
  const previousFigures = previous.data?.figures ?? null
  const useFallback = currentEmpty && previousFigures !== null && !periodIsEmpty(previousFigures)
  const wording = useBoardWording(useFallback ? tile.period : null, useFallback)
  const link = useLink(OverviewRoutes.accounts, t("branch.treasury.link"))
  const record = useRecordLink()
  const title = t("widgets.branchTreasury.title")
  return (
    <TileStates
      id={FinanceWidgetId.BranchTreasury}
      title={title}
      isLoading={current.isLoading || (currentEmpty && previous.isLoading)}
      isError={current.isError}
      onRetry={() => void current.refetch()}
    >
      {() =>
        !figures || !current.data ? (
          <WithheldTile title={title} />
        ) : (
          <BranchTreasuryTile
            figures={figures}
            period={useFallback && previousFigures ? previousFigures : figures}
            accounts={board.data?.figures?.accounts ?? null}
            currency={current.data.currency}
            wording={wording}
            link={link}
            record={record}
          />
        )
      }
    </TileStates>
  )
}

export function BranchAttentionWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const board = useBranchBoardQuery({ enabled: true })
  const trial = useAccountingTrialBalanceQuery(
    scope.workspaceId && scope.branchId ? { workspaceId: scope.workspaceId, branchId: scope.branchId } : null,
  )
  const wording = useBoardWording(null, false)
  const go = useOverviewNav()
  const title = t("widgets.branchAttention.title")
  const figures = board.data?.figures ?? null
  return (
    <TileStates
      id={FinanceWidgetId.BranchAttention}
      title={title}
      isLoading={board.isLoading}
      isError={board.isError}
      onRetry={() => void board.refetch()}
    >
      {() =>
        !figures || !board.data ? (
          <WithheldTile title={title} />
        ) : (
          <BranchAttentionTile
            attention={figures.attention}
            accounts={figures.accounts}
            balanced={trial.data ? Boolean(trial.data.balanced) : null}
            currency={board.data.currency}
            during={wording.during}
            hrefs={{
              evidence: go.hrefFor(OverviewRoutes.investigations),
              reconcile: go.hrefFor(OverviewRoutes.reconciliation),
              transfers: go.hrefFor(OverviewRoutes.transfers),
              ledger: go.hrefFor(OverviewRoutes.investigations),
            }}
          />
        )
      }
    </TileStates>
  )
}

/** The three period tiles fed by the board's breakdowns share one shape: fallback, compact empty, withheld. */
function BreakdownWidget({
  id,
  titleKey,
  emptyKey,
  isEmpty,
  render,
}: {
  id: FinanceWidgetId
  titleKey: string
  emptyKey: string
  isEmpty: (figures: BranchFinanceBoardFigures) => boolean
  render: (data: ReturnType<typeof useBoardWithFallback> & { figures: BranchFinanceBoardFigures }, wording: ReturnType<typeof useBoardWording>) => ReactNode
}) {
  const t = useTranslations("finance.overview")
  const data = useBoardWithFallback(isEmpty)
  const wording = useBoardWording(data.shownPeriod, data.fallback !== null)
  const record = useRecordLink()
  const title = t(titleKey)
  return (
    <TileStates
      id={id}
      title={title}
      isLoading={data.isLoading}
      isError={data.current.isError}
      onRetry={() => void data.current.refetch()}
      compact={data.bothEmpty}
    >
      {() =>
        data.withheld || !data.figures ? (
          <WithheldTile title={title} />
        ) : data.bothEmpty ? (
          <ChurchTileCompactEmpty title={title} message={t(emptyKey)} action={record} />
        ) : (
          render({ ...data, figures: data.figures }, wording)
        )
      }
    </TileStates>
  )
}

export function BranchIncomeWidget() {
  const t = useTranslations("finance.overview")
  const link = useLink(OverviewRoutes.transactions, t("branch.detail"))
  return (
    <BreakdownWidget
      id={FinanceWidgetId.BranchIncome}
      titleKey='widgets.branchIncome.title'
      emptyKey='branch.income.empty'
      isEmpty={(figures) => isEmptyBreakdown(figures.income)}
      render={(data, wording) => (
        <BranchIncomeTile income={data.figures.income} currency={data.currency} wording={wording} fallback={data.fallback} link={link} />
      )}
    />
  )
}

export function BranchExpensesWidget() {
  const t = useTranslations("finance.overview")
  const link = useLink(OverviewRoutes.transactions, t("branch.detail"))
  return (
    <BreakdownWidget
      id={FinanceWidgetId.BranchExpenses}
      titleKey='widgets.branchExpenses.title'
      emptyKey='branch.expenses.empty'
      isEmpty={(figures) => isEmptyBreakdown(figures.expenses)}
      render={(data, wording) => (
        <BranchExpensesTile expenses={data.figures.expenses} currency={data.currency} wording={wording} fallback={data.fallback} link={link} />
      )}
    />
  )
}

export function BranchMarginWidget() {
  return (
    <BreakdownWidget
      id={FinanceWidgetId.BranchMargin}
      titleKey='widgets.branchMargin.title'
      emptyKey='branch.margin.empty'
      isEmpty={(figures) => figures.margin.spentPer100 === null}
      render={(data, wording) => (
        <BranchMarginTile margin={data.figures.margin} currency={data.currency} wording={wording} fallback={data.fallback} />
      )}
    />
  )
}

export function BranchBudgetWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  // No period: the whole plan of every budget in progress against what is realised to date (ADR-0011).
  const query = useBudgetPerformanceQuery(
    { workspaceId: scope.workspaceId ?? undefined, context: BudgetScope.Branch, branchId: scope.branchId ?? undefined },
    { enabled: Boolean(scope.workspaceId && scope.branchId) },
  )
  const link = useLink(OverviewRoutes.budgets, t("branch.budget.link"))
  const createHref = scope.canEdit ? go.hrefFor(OverviewRoutes.createBudget) : null
  const title = t("widgets.branchBudget.title")
  const data = query.data
  const empty = data !== undefined && (data.includedBudgetIds.length === 0 || data.lines.length === 0)
  return (
    <TileStates
      id={FinanceWidgetId.BranchBudget}
      title={title}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      compact={empty}
    >
      {() => {
        if (!data || empty) {
          return (
            <ChurchTileCompactEmpty
              title={title}
              message={t("branch.budget.empty")}
              action={createHref ? { label: t("branch.budget.create"), href: createHref } : null}
            />
          )
        }
        const pace = budgetPacePercent(data.reportingPeriod, new Date())
        const single = data.includedBudgetIds.length === 1 ? data.lines[0]?.budgetTitle : undefined
        return (
          <BranchBudgetTile
            title={single ?? title}
            pace={pace}
            expenseRows={budgetRows(data.lines, BudgetLineType.Expense, pace, data.currency)}
            incomeRows={budgetRows(data.lines, BudgetLineType.Income, pace, data.currency)}
            currency={data.currency}
            link={link}
          />
        )
      }}
    </TileStates>
  )
}

export function BranchFundsWidget() {
  const t = useTranslations("finance.overview")
  const board = useBranchBoardQuery({ enabled: true })
  const link = useLink(OverviewRoutes.funds, t("branch.funds.link"))
  const title = t("widgets.branchFunds.title")
  return (
    <TileStates
      id={FinanceWidgetId.BranchFunds}
      title={title}
      isLoading={board.isLoading}
      isError={board.isError}
      onRetry={() => void board.refetch()}
    >
      {() =>
        board.data?.figures ? (
          <BranchFundsTile funds={board.data.figures.funds} currency={board.data.currency} link={link} />
        ) : (
          <WithheldTile title={title} />
        )
      }
    </TileStates>
  )
}

export function BranchPledgesWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const base = scope.workspaceId && scope.branchId
    ? {
        workspaceId: scope.workspaceId,
        branchId: scope.branchId,
        // Money only: goods are counted in units and are never added to money.
        kind: PledgeKind.Money,
        fulfillment: PledgeFulfillmentFilter.Pending,
      }
    : null
  const open = usePledgesQuery(base ? { ...base, sortBy: "amountPledged", sortOrder: "desc", limit: 2 } : null)
  const late = usePledgesQuery(base ? { ...base, dueAtTo: localDayBefore(new Date()), limit: 1 } : null)
  const link = useLink(OverviewRoutes.pledges, t("branch.pledges.link"))
  const lateHref = go.hrefFor(OverviewRoutes.pledges)
  const createHref = scope.canEdit ? go.hrefFor(OverviewRoutes.createPledge) : null
  const title = t("widgets.branchPledges.title")
  const summary = open.data?.summary
  const empty = summary !== undefined && summary.count === 0
  return (
    <TileStates
      id={FinanceWidgetId.BranchPledges}
      title={title}
      isLoading={open.isLoading}
      isError={open.isError}
      onRetry={() => void open.refetch()}
      compact={empty}
    >
      {() =>
        !summary || empty ? (
          <ChurchTileCompactEmpty
            title={title}
            message={t("branch.pledges.empty")}
            action={createHref ? { label: t("branch.pledges.create"), href: createHref } : null}
          />
        ) : (
          <BranchPledgesTile
            received={summary.totalReceived}
            pledged={summary.totalPledged}
            fulfillment={summary.fulfillmentPercentage}
            count={summary.count}
            late={late.data?.summary.count ?? 0}
            top={(open.data?.items ?? []).map((pledge) => ({
              id: pledge.id,
              name: pledge.contributor?.name ?? pledge.pledgerName ?? t("branch.pledges.anonymous"),
              received: pledge.amountReceived,
              pledged: pledge.amountPledged,
            }))}
            currency={summary.currency}
            link={link}
            lateLink={lateHref ? { label: t("branch.pledges.seeLate"), href: lateHref } : null}
          />
        )
      }
    </TileStates>
  )
}

export function BranchRecentWidget({ context }: WidgetProps) {
  const t = useTranslations("finance.overview")
  const locale = useLocale()
  const scope = useFinanceOverviewScope()
  const tile = usePeriodFallback()
  const current = useRecentFinanceTransactionRows({ context, limit: RECENT_OPERATION_ROWS })
  const currentEmpty = !current.query.isLoading && !current.query.isError && current.query.data !== undefined && current.rows.length === 0
  const previous = useRecentFinanceTransactionRows({
    context,
    limit: RECENT_OPERATION_ROWS,
    period: tile.period ?? undefined,
    enabled: currentEmpty && tile.period !== null,
  })
  const useFallback = currentEmpty && previous.rows.length > 0
  const bothEmpty = currentEmpty && !previous.query.isLoading && !useFallback
  const wording = useBoardWording(null, false)
  const link = useLink(OverviewRoutes.transactions, t("branch.recent.link"))
  const record = useRecordLink()
  const title = t("widgets.branchRecent.title")
  return (
    <TileStates
      id={FinanceWidgetId.BranchRecent}
      title={title}
      isLoading={current.query.isLoading || (currentEmpty && previous.query.isLoading)}
      isError={current.query.isError}
      onRetry={() => void current.query.refetch()}
      compact={bothEmpty}
    >
      {() =>
        current.withheld ? (
          <WithheldTile title={title} />
        ) : bothEmpty ? (
          <ChurchTileCompactEmpty title={title} message={t("branch.recent.empty")} action={record} />
        ) : (
          <BranchRecentTile
            rows={useFallback ? previous.rows : current.rows}
            currency={scope.currency}
            subtitle={capitalize(wording.during, locale)}
            fallback={useFallback ? tile.fallback : null}
            link={link}
          />
        )
      }
    </TileStates>
  )
}

export function BranchReconciliationWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const board = useBranchBoardQuery({ enabled: true })
  const go = useOverviewNav()
  const href = go.hrefFor(OverviewRoutes.reconciliation)
  const title = t("widgets.branchReconciliation.title")
  return (
    <TileStates
      id={FinanceWidgetId.BranchReconciliation}
      title={title}
      isLoading={board.isLoading}
      isError={board.isError}
      onRetry={() => void board.refetch()}
    >
      {() =>
        board.data?.figures ? (
          <BranchReconciliationTile
            accounts={board.data.figures.accounts}
            action={href ? { label: t(scope.canEdit ? "branch.reconciliation.start" : "branch.reconciliation.see"), href } : null}
          />
        ) : (
          <WithheldTile title={title} />
        )
      }
    </TileStates>
  )
}

export function BranchControlWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const board = useBranchBoardQuery({ enabled: true })
  const target = scope.workspaceId && scope.branchId ? { workspaceId: scope.workspaceId, branchId: scope.branchId } : null
  const trial = useAccountingTrialBalanceQuery(target)
  const signals = useFinanceInvestigationSignalsQuery(target ? { ...target, scope: "BRANCH" } : null)
  const wording = useBoardWording(null, false)
  const link = useLink(OverviewRoutes.investigations, t("branch.detail"))
  const title = t("widgets.branchControl.title")
  const refetch = React.useCallback(() => {
    void board.refetch()
    void trial.refetch()
    void signals.refetch()
  }, [board, trial, signals])
  return (
    <TileStates
      id={FinanceWidgetId.BranchControl}
      title={title}
      isLoading={board.isLoading || trial.isLoading || signals.isLoading}
      isError={board.isError || trial.isError || signals.isError}
      onRetry={refetch}
    >
      {() =>
        board.data?.figures && trial.data && signals.data ? (
          <BranchControlTile
            balanced={Boolean(trial.data.balanced)}
            reviewable={signals.data.reversed.count + signals.data.voided.count}
            missingInPeriod={board.data.figures.attention.missingEvidenceInPeriod}
            toReconcile={board.data.figures.attention.accountsToReconcile}
            during={wording.during}
            link={link}
          />
        ) : (
          <WithheldTile title={title} />
        )
      }
    </TileStates>
  )
}
